import React, { useState, useEffect } from 'react'
import { components } from 'react-select'
import { SimpleSelect, AsyncSimpleSelect } from '../Select'
import { Col } from '../Globals'
import { Column, Row } from '@components/Common'
// import { FullWidthButton } from '../Button'
import { colors, sizes } from '@theme'
// import Icon from '../Icon'
import {
  useMediaQuery,
  useTranslatedTerms,
  useWindowScrollPosition,
} from '@hooks'

const colourStyles = {
  control: styles => ({ ...styles, backgroundColor: 'white' }),
  multiValue: (styles, { data }) => {
    return {
      ...styles,
      backgroundColor: data.bgColor || colors.purple,
      borderRadius: '3px',
    }
  },
  multiValueLabel: (styles, { data }) => {
    return {
      ...styles,
      color: data.color || 'white',
      padding: '5px',
    }
  },
  multiValueRemove: (styles, { data }) => {
    return {
      ...styles,
      color: data.color || 'white',
      ':hover': {
        backgroundColor: data.hoverBgColor || colors.purple,
        color: data.hoverColor || 'white',
        cursor: 'pointer',
      },
    }
  },
}

const ClearIndicator = mobile => {
  const ClearIndicatorComponent = props => (
    <components.ClearIndicator {...props}>
      {!mobile ? 'Reset Filters' : 'Reset'}
    </components.ClearIndicator>
  )
  ClearIndicatorComponent.displayName = 'ClearIndicator'
  return ClearIndicatorComponent
}

const DropdownIndicator = () => null

const Placeholder = props => {
  const { getTranslatedTerm } = useTranslatedTerms()
  return (
    <components.Placeholder {...props}>
      {getTranslatedTerm('Search By Keyword')}
    </components.Placeholder>
  )
}

const filterGroupedOption = (inputValue, groupedOption) => {
  const newOptions = groupedOption.options.filter(
    option =>
      option.label &&
      option.label?.toLowerCase().includes(inputValue?.toLowerCase())
  )
  return { ...groupedOption, options: newOptions }
}

const filterOptions = (inputValue, groupedOptions) =>
  groupedOptions.map(groupedOption =>
    filterGroupedOption(inputValue, groupedOption)
  )

const promiseOptions = (options, delay = 1000) => inputValue =>
  new Promise(resolve => {
    setTimeout(() => {
      resolve(filterOptions(inputValue, options))
    }, delay)
  })

const SelectBoxOne = ({
  data,
  placeholder,
  dropdownClassName,
  filterByField,
  absolute,
}) => (
  <SimpleSelect
    widthPercentage={100}
    closeMenuOnSelect
    components={{ ClearIndicator }}
    isSearchable
    options={data || null}
    placeholder={placeholder}
    onChange={filterByField}
    dropdownClassName={dropdownClassName}
    absolute={absolute}
  />
)

const SearchAndFilterDisplay = ({
  handleSelect,
  data,
  filters,
  onInputChange,
  mobile,
}) => {
  return (
    <AsyncSimpleSelect
      components={{
        Placeholder,
        ClearIndicator: ClearIndicator(mobile),
        DropdownIndicator,
      }}
      isMulti
      cacheOptions
      defaultOptions={[]}
      loadOptions={promiseOptions(data, 500)}
      styles={colourStyles}
      placeholder="Search"
      value={filters}
      onChange={handleSelect}
      classNamePrefix="react-select"
      className="react-select-container"
      staticIndicators
      isValid
    />
  )
}

const filtersToQueryString = filters => {
  const params = new URLSearchParams()
  filters.forEach(filter => {
    const type = filter.group?.toLowerCase()
    if (type) {
      params.append(type, filter.value)
    }
  })
  return params.toString()
}

const queryParamsToFilters = (location, groupedOptions) => {
  if (!location?.search) return []

  const params = new URLSearchParams(location.search)
  const filters = []

  groupedOptions.forEach(group => {
    const type = group.label.toLowerCase()
    const values = params.getAll(type)

    values.forEach(value => {
      const option = group.options.find(opt => opt.value === value)
      if (option) {
        filters.push({ ...option, group: group.label })
      }
    })
  })

  return filters
}

const KeyUpSearch = ({
  groupedOptions,
  mobile,
  handleRerender,
  filterAlignBetween,
  xs = 6,
  sm = 6,
  md = 4,
  lg = 4,
  xl = 2,
  insights = false,
  handleSearchButton,
  resetFilters,
  startingFilter,
  location,
}) => {
  const [filters, setFilters] = useState(() => {
    const urlFilters = queryParamsToFilters(location, groupedOptions)
    return urlFilters.length > 0
      ? urlFilters
      : startingFilter
      ? [startingFilter]
      : []
  })

  // const { getTranslatedTerm } = useTranslatedTerms()

  const filterByField = newFilter => {
    const filterCurrentIndex = filters.findIndex(
      filter => filter.value === newFilter.value
    )
    const filterHasBeenAlreadyApplied = filterCurrentIndex !== -1

    if (filterHasBeenAlreadyApplied) {
      return
    }
    const filterWithGroup = {
      ...newFilter,
      group: groupedOptions.find(group =>
        group.options.some(opt => opt.value === newFilter.value)
      )?.label,
    }
    setFilters([...filters, filterWithGroup])
  }

  useEffect(() => {
    const isInsightsPage = location?.pathname === '/insights'
    const hasFilters = filters.length > 0
    const hasSearchParams = location?.search

    if (isInsightsPage && hasFilters && !hasSearchParams) {
      setFilters([])
    }
  }, [location?.pathname, location?.search])

  // Use the window scroll position hook to track scroll position
  const scrollPosition = useWindowScrollPosition()

  // Set history.scrollRestoration to 'manual' on component mount
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      'scrollRestoration' in window.history
    ) {
      // Save the original value to restore it on unmount
      const originalScrollRestoration = window.history.scrollRestoration
      window.history.scrollRestoration = 'manual'

      return () => {
        // Restore the original value when component unmounts
        window.history.scrollRestoration = originalScrollRestoration
      }
    }
  }, [])

  useEffect(() => {
    handleRerender(filters)

    const queryString = filtersToQueryString(filters)
    const newUrl = `${location?.pathname || ''}${
      queryString ? `?${queryString}` : ''
    }`

    if (
      typeof window !== 'undefined' &&
      window.location.pathname + window.location.search !== newUrl
    ) {
      // Use history API directly with replaceState to avoid navigation
      window.history.replaceState(
        // Store the scroll position in the state object
        { scrollY: scrollPosition.y },
        '',
        newUrl
      )

      // Immediately restore scroll position
      window.scrollTo(0, scrollPosition.y)

      // Also restore after a short delay to handle any post-render adjustments
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPosition.y)
      })
    }
  }, [filters, handleRerender, location?.pathname, scrollPosition.y])

  useEffect(() => {
    if (resetFilters) setFilters([])
  }, [resetFilters])

  const filterByAll = (filtersArray, { action }) => {
    if (action === 'menu-close') return

    if (!Array.isArray(filtersArray) || filtersArray.length === 0) {
      setFilters([])
    } else {
      const filtersWithGroups = filtersArray.map(filter => ({
        ...filter,
        group: groupedOptions.find(group =>
          group.options.some(opt => opt.value === filter.value)
        )?.label,
      }))
      setFilters(filtersWithGroups)
    }
  }

  const insightsLg = useMediaQuery(`(min-width: ${sizes.large.min})`)
  const filterMarginRight =
    insights && insightsLg && filterAlignBetween ? '10px' : '0px'
  const alignBetween = !insights || !insightsLg

  return (
    <>
      <Row className="justify-center">
        <Column>
          <SearchAndFilterDisplay
            handleSelect={filterByAll}
            data={groupedOptions}
            filters={filters}
            mobile={mobile}
          />
        </Column>
      </Row>
      <Row className={`${alignBetween ? 'justify-between' : null}`}>
        {!mobile &&
          groupedOptions
            .filter(field => field.label !== 'Name')
            .map(field => {
              const placeholder =
                field.label.length > 13
                  ? `${field.label.slice(
                      0,
                      field.label.length / 2
                    )} ${field.label.slice(field.label.length / 2)}`
                  : field.label

              return (
                <Col
                  xs={xs - 0.1}
                  sm={sm - 0.1}
                  md={md - 0.1}
                  lg={lg - 0.1}
                  xl={xl - 0.1}
                  marginTop={10}
                  key={field.label}
                  style={{ marginRight: filterMarginRight }}
                >
                  <SelectBoxOne
                    filterByField={filterByField}
                    data={field.options}
                    filters={filters}
                    placeholder={placeholder}
                    key={field.label}
                    style={{ marginRight: '10px' }}
                    dropdownClassName={field.dropdownClassName}
                    absolute="true"
                  />
                </Col>
              )
            })}
        <Col
          xs={xs - 0.1}
          sm={sm - 0.1}
          md={md - 0.1}
          lg={lg - 0.1}
          xl={xl - 0.1}
          marginTop={10}
        >
          {/* <FullWidthButton
            className="qtph-prof-search-btn items-center justify-center flex cursor-pointer"
            active
            style={{
              width: '100%',
              height: '100%',
              fontSize: '14px',
              borderRadius: '3px',
            }}
            color="white"
            onClick={handleSearchButton || (() => {})}
          >
            <Icon glyph="search" height={25} width={25} />
            <BodyCopy
              fontSize={14}
              style={{
                fontWeight: 'bold',
                textTransform: 'uppercase',
                paddingRight: '10px',
              }}
            >
              {getTranslatedTerm('Submit')}
            </BodyCopy>
          </FullWidthButton> */}
        </Col>
      </Row>
    </>
  )
}

export default KeyUpSearch
